'use client'
import { cn } from '@/utilities/ui'
import useClickableCard from '@/utilities/useClickableCard'
import Link from 'next/link'
import React, { Fragment, useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'

import type { Angebote, Post } from '@/payload-types'

import { Media } from '@/components/Media'
import { useInView } from 'react-intersection-observer'
import { Card, CardContent, CardFooter } from '../ui/card'
import { Calendar } from 'lucide-react'
import { Button } from '../ui/button'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { imgproxyUrlFromMediaUrl } from '@/utilities/imgproxy'

export type CardPostData = Pick<
  Angebote,
  'slug' | 'meta' | 'title' | 'publishedAt' | 'angebotShortDescription' | 'image'
>

export const OfferCard: React.FC<{
  alignItems?: 'center'
  className?: string
  doc?: CardPostData
  relationTo?: 'angebote'
  showCategories?: boolean
  title?: string
  publishedAt?: string
}> = (props) => {
  const sectionRef = useRef<HTMLElement | null>(null)
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })
  const { card, link } = useClickableCard({})

  const { className, doc, relationTo, showCategories, title: titleFromProps } = props

  const { slug, meta, title, publishedAt, angebotShortDescription, image } = doc || {}
  const { description, image: metaImage } = meta || {}

  const titleToUse = titleFromProps || title
  const sanitizedDescription = description?.replace(/\s/g, ' ') // replace non-breaking space with white space
  const href = `/${relationTo}/${slug}`

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '5%'])

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
    },
    transition: (i: number) => ({
      duration: 0.8,
      delay: i * 0.1,
      ease: 'easeOut',
    }),
  }

  const imageVariants = {
    hidden: { scale: 1.2, opacity: 0.8 },
    visible: {
      scale: 1,
      opacity: 1,
    },
    transition: {
      duration: 1.2,
      ease: 'easeOut',
    },
  }

  return (
    <motion.article
      className={cn(
        'h-full overflow-hidden border-0 shadow-md transition-shadow duration-500 hover:shadow-xl  bg-white  ',
        className,
      )}
      ref={(node) => {
        sectionRef.current = node
        card.ref.current = node
      }}
    >
      <Link href={href}>
        <motion.div variants={itemVariants}>
          <Card className="shadow-xs h-full overflow-hidden border-0 transition-shadow duration-500 hover:shadow-md">
            <div className="h-52 overflow-hidden">
              <motion.img
                variants={imageVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
                src={imgproxyUrlFromMediaUrl(getMediaUrl(image), { width: 800 })}
                alt={title}
                className="h-full w-full object-cover"
              />
            </div>
            <CardContent className="p-8">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mb-4 flex items-center text-sm font-light text-gray-500"
              >
                <Calendar className="mr-3 h-4 w-4 text-antonie-brown" />
                <span>{publishedAt}</span>
              </motion.div>

              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-4 line-clamp-2 font-serif text-xl font-medium text-gray-900"
              >
                {title}
              </motion.h3>

              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="line-clamp-3 font-light text-gray-600"
              >
                {angebotShortDescription}
              </motion.p>
            </CardContent>
            <CardFooter className="px-8 pb-8 pt-0">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="w-full"
              >
                <Button
                  variant="outline"
                  className="w-full border-2 border-antonie-green text-black transition-all duration-300 hover:bg-antonie-green hover:text-black"
                >
                  Weiterlesen
                </Button>
              </motion.div>
            </CardFooter>
          </Card>
        </motion.div>
      </Link>
    </motion.article>
  )
}
